<script setup lang="ts">
/**
 * One conversation of a chat list: avatar, name, time of the last message,
 * its preview („Du: …“, „Timo: …“) and the unread counter.
 *
 * Presentational only — no role, no focus, no click handling of its own.
 * `NxChatChannelList` wraps it in a listbox option; another host wraps it in
 * its own control. The `#avatar` slot replaces the avatar.
 */
import { computed } from 'vue';
import NxAvatar from '../Avatar/NxAvatar.vue';
import type { NxChatAvatar } from './chatList';

const props = withDefaults(
  defineProps<{
    name: string;
    preview?: string;
    /** Shown before the preview: „Du“, a first name. */
    previewAuthor?: string;
    time?: string;
    unread?: number;
    avatar?: NxChatAvatar;
    /** Logical px of the avatar. */
    avatarSize?: number;
    /** The open conversation. Only styles the row; the host sets the semantics. */
    active?: boolean;
    unreadLabel?: (count: number) => string;
    /** Regular name; medium only while active or unread. */
    quiet?: boolean;
  }>(),
  {
    preview: undefined,
    previewAuthor: undefined,
    time: undefined,
    unread: 0,
    avatar: undefined,
    avatarSize: 30,
    active: false,
    unreadLabel: (count: number) => `${count} ungelesen`,
  }
);

defineSlots<{
  avatar?(): unknown;
  /** Fades in over the end of the preview while the pointer is on the row. */
  action?(): unknown;
}>();

/** The counter stays narrow: „99+“ from a hundred on. */
const unreadText = computed(() => (props.unread > 99 ? '99+' : String(props.unread)));
</script>

<template>
  <div
    class="nx-chat-list-item"
    :class="{ 'is-active': active, 'has-unread': unread > 0, 'is-quiet': quiet }"
  >
    <slot name="avatar">
      <NxAvatar
        :name="name"
        :size="avatarSize"
        :initials="avatar?.initials"
        :hue="avatar?.hue"
        :tone="avatar?.tone"
        :emphasis="avatar?.emphasis"
        :shape="avatar?.shape"
        decorative
      />
    </slot>
    <div class="nx-chat-list-item__main">
      <div class="nx-chat-list-item__top">
        <span class="nx-chat-list-item__name" :title="name">{{ name }}</span>
        <span v-if="time" class="nx-chat-list-item__time">{{ time }}</span>
      </div>
      <div v-if="quiet" class="nx-chat-list-item__line">
        <div class="nx-chat-list-item__text">
          <div
            class="nx-chat-list-item__preview"
            :title="previewAuthor ? `${previewAuthor}: ${preview ?? ''}` : preview"
          >
            <span v-if="previewAuthor" class="nx-chat-list-item__author">
              {{ `${previewAuthor}:` }}
            </span>
            {{ preview }}
          </div>
          <span v-if="$slots.action" class="nx-chat-list-item__action" @click.stop>
            <slot name="action" />
          </span>
        </div>
        <span v-if="unread > 0" class="nx-chat-list-item__unread">
          <span aria-hidden="true">{{ unreadText }}</span>
          <span class="nx-chat-list-item__sr">{{ unreadLabel(unread) }}</span>
        </span>
      </div>
      <div
        v-else-if="preview"
        class="nx-chat-list-item__preview"
        :title="previewAuthor ? `${previewAuthor}: ${preview}` : preview"
      >
        <span v-if="previewAuthor" class="nx-chat-list-item__author">
          {{ `${previewAuthor}:` }}
        </span>
        {{ preview }}
      </div>
    </div>
    <span v-if="unread > 0 && !quiet" class="nx-chat-list-item__unread">
      <span aria-hidden="true">{{ unreadText }}</span>
      <span class="nx-chat-list-item__sr">{{ unreadLabel(unread) }}</span>
    </span>
  </div>
</template>

<style scoped>
.nx-chat-list-item {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  min-width: 0;
}
.nx-chat-list-item__main {
  flex: 1;
  min-width: 0;
}
.nx-chat-list-item__top {
  display: flex;
  align-items: baseline;
  gap: var(--nxt-space-xs);
}
.nx-chat-list-item__name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-semibold);
  color: var(--nxt-fg);
}
.nx-chat-list-item__time {
  flex: none;
  font-size: var(--nxt-font-size-xs);
  font-variant-numeric: tabular-nums;
  color: var(--nxt-fg-muted);
}
.nx-chat-list-item__line {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  min-width: 0;
  min-height: calc(var(--nxt-size-unit) * 1.125);
}
.nx-chat-list-item__text {
  position: relative;
  flex: 1;
  min-width: 0;
}
/* The action lies over the end of the preview: nothing in the row moves or
   changes size, it only fades in. The buttons may hang over the line. */
.nx-chat-list-item__action {
  position: absolute;
  inset-block: 0;
  inset-inline-end: 0;
  display: inline-flex;
  align-items: center;
  padding-inline-start: var(--nxt-space-xs);
  background: var(--chat-list-row-bg, var(--nxt-surface-bg));
  opacity: 0;
  pointer-events: none;
  transition: opacity var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-chat-list-item:hover .nx-chat-list-item__action,
.nx-chat-list-item:focus-within .nx-chat-list-item__action {
  opacity: 1;
  pointer-events: auto;
  transition-delay: var(--nxt-motion-fast);
}
.nx-chat-list-item.is-quiet .nx-chat-list-item__name {
  font-weight: var(--nxt-font-weight-regular);
}
.nx-chat-list-item.is-quiet.is-active .nx-chat-list-item__name,
.nx-chat-list-item.is-quiet.has-unread .nx-chat-list-item__name {
  font-weight: var(--nxt-font-weight-medium);
}
.nx-chat-list-item.is-quiet.has-unread .nx-chat-list-item__preview {
  color: var(--nxt-fg);
}
.nx-chat-list-item__preview {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-fg-muted);
}
.nx-chat-list-item.has-unread .nx-chat-list-item__preview {
  color: var(--nxt-fg-subtle);
}
.nx-chat-list-item__author {
  font-weight: var(--nxt-font-weight-medium);
  color: var(--nxt-fg-subtle);
}
.nx-chat-list-item__unread {
  position: relative;
  flex: none;
  display: inline-grid;
  place-items: center;
  min-width: calc(var(--nxt-size-unit) * 1.125);
  height: calc(var(--nxt-size-unit) * 1.125);
  padding-inline: var(--nxt-space-2xs);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-brand-bg);
  color: var(--nxt-brand-fg);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-bold);
  font-variant-numeric: tabular-nums;
  line-height: 1;
}
.nx-chat-list-item__sr {
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  margin: calc(var(--nxt-hairline) * -1);
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}
@media (prefers-reduced-motion: reduce) {
  .nx-chat-list-item__action {
    transition: none;
  }
}
</style>
